// src/belediye/components/BelediyeNavbar.js
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import {
  EllipsisVerticalIcon,
  HomeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  BellIcon, // mobil menüde sadece ikon olarak kullanacağız
  Bars3Icon, // FAZ-88: dar katmanda sol yuva — çekmeceyi açar
  ChevronLeftIcon, // FAZ-88: detay sayfasında sol yuva — geri
  ChevronDownIcon, // FAZ-91: orta yuva taraf seçicisi
} from "@heroicons/react/24/outline";

// FAZ-88: ekran katmanı ve detay başlığı köprüsü
import useKatman from "../hooks/useKatman";
import { useNavbarBaslik } from "./NavbarBaslikContext";

// ─────────────────────────────────────────────────────────────────────────────
// TEK ÇIKIŞ KAYNAĞI
// performLogout → backend bildirimi + localStorage temizleme + redirect
// Hem desktop buton hem mobil menü hem de eski handleLogout kullanımı
// burayı çağırır.
// ─────────────────────────────────────────────────────────────────────────────
import LogoutButton, { performLogout } from "./BelediyeLogoutButton";

// Arsuz Belediyesi — logo (belediye marka logosu)
import kimLogo from "../assets/images/kim_logo.png";
import "./BelediyeNavbar.css";

import { useSelector, useDispatch } from "react-redux";
import { fetchMyProfile } from "../redux/slices/profileSlice";

//   SessionRole tek kaynak
import {
  getSessionRole,
  isPresident,
  isFieldStaff,
  isSupervisor, //   NEW: müdür etiketi için
  isDeskStaff,  //   NEW: halk masası tespiti için (utils’te var)
} from "../utils/sessionRole";

//   Tek kaynak bildirim UI (dropdown + badge + “Tümü”)
import NotificationsBell from "./notifications/NotificationsBell";

//   Mesajlaşma ikonu — sohbet panelini açar. FAZ-51: ROZETİ YOK; okunmamış
//   mesaj artık bildirim üretiyor ve zilde sayılıyor (tek sayaç).

//   İzin/kapsam: all_units aktör tespiti + global rol adı (navbar alt-başlık)
import usePermissions from "../hooks/usePermissions";

// ─────────────────────────────────────────────────────────────────────────────
// localStorage anahtarları
// NOT: accessToken/refreshToken/user/legacyToken artık BURADA temizlenmiyor.
//      Onları performLogout (BelediyeLogoutButton.js) hallediyor.
//      Burada sadece "active unit" (birim seçimi) ile ilgili alanlar var,
//      çünkü bunlar Navbar'a özgü (clearActiveUnit kullanılıyor).
// ─────────────────────────────────────────────────────────────────────────────
const LS = {
  activeUnitId: "activeUnitId",
  activeUnitSlug: "activeUnitSlug",
  activeEffectiveRole: "activeEffectiveRole",
};

const clearActiveUnit = () => {
  localStorage.removeItem(LS.activeUnitId);
  localStorage.removeItem(LS.activeUnitSlug);
  localStorage.removeItem(LS.activeEffectiveRole);
};

const safeGetActiveEffectiveRole = () => {
  try {
    return String(localStorage.getItem(LS.activeEffectiveRole) || "").trim();
  } catch {
    return "";
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// FAZ-88 — DETAY ROTALARI
// Sol yuvanın hamburger mi geri oku mu olacağını belirler.
//
// İki kaynak birlikte çalışır:
//   1. Sayfa kendini bildirdiyse (NavbarBaslikContext) → kesin detay.
//   2. Bildirmediyse rota desenine bakılır → hiçbir detay sayfası
//      geri okusuz kalmaz (bildirme adımı unutulsa bile).
//
// `(?:[^/]+\/)?` kısmı legacy `:unitSlug` önekli rotaları da kapsar:
// hem /yonetim/basvurular/4471 hem /yonetim/fen/basvurular/4471.
// ─────────────────────────────────────────────────────────────────────────────
const DETAY_DESENLERI = [
  /^\/yonetim\/(?:[^/]+\/)?basvurular\/[^/]+$/,
  /^\/yonetim\/(?:[^/]+\/)?gorevler\/[^/]+$/,
  /^\/yonetim\/(?:[^/]+\/)?personel\/[^/]+$/,
  /^\/yonetim\/(?:[^/]+\/)?anketler\/[^/]+$/,
  /^\/yonetim\/halk-masasi\/(?:basvuru|vatandas)\/[^/]+$/,
  /^\/yonetim\/saha\/gorevlerim\/[^/]+$/,
  /^\/yonetim\/[^/]+\/saha\/[^/]+$/,
  /^\/yonetim\/sistem\/(?:kullanicilar|roller|vatandaslar)\/[^/]+$/,
  // FAZ-94 — sohbet alt rotaları. "/sohbet" LİSTEDİR (geri oku yok); "yeni" ve
  // ":conversationId" detaydır. Detay sayfasında navbar zaten hiç çizilmiyor,
  // desen yine de duruyor: kayan-navbar kancası (FAZ-93) "detayda gizleme yok"
  // kuralını buradan okuyor.
  /^\/yonetim\/sohbet\/[^/]+$/,
];

export function detayRotasiMi(pathname) {
  const yol = String(pathname || "").replace(/\/+$/, "") || "/";
  return DETAY_DESENLERI.some((d) => d.test(yol));
}

const BelediyeNavbar = ({
  user,
  unitName,
  unitSlug,
  onSidebarToggle,
  sidebarEnabled = true,
  sidebarExpanded = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  /* FAZ-88 — dar katmanda navbar üç yuvaya iner. Masaüstünde tek piksel
     değişmez: logo, birim değiştir, profil ve çıkış aynen durur.

     FAZ-98/T-1 — TABLET BU DALDAN ÇIKTI.
     Kullanıcı (2026-08-02): "tablet modunda profil ve çıkış yapma herhangi
     bir yerde yok… tablet modunun web'deki navbar'ı kullanalım, işte logosu
     şeyiyle birlikte ve sidebar'ıyla birlikte aynı mantık olsun."

     GERÇEK HATA — TABLETTE ÇIKIŞ YOLU YOKTU: profil ve "Çıkış yap" FAZ-88'de
     navbardan alınıp alt bardaki profil yuvasına taşındı; alt bar ise FAZ-90'da
     YALNIZ telefona verildi (AltBar.js:181). Sidebar'da da profil/çıkış maddesi
     yok. İki kararın kesiştiği yerde tablet kullanıcısı oturumunu kapatamıyor,
     profiline giremiyordu. Dar navbar bir alt bar VARSAYIYOR; o varsayım yalnız
     telefonda doğru. */
  const katmanAdi = useKatman();
  const dar = katmanAdi === "telefon";
  const { baslik } = useNavbarBaslik();
  /* FAZ-92 — GERİ OKU yalnız KAYIT başlığında ya da detay rotasında.
     PageHeader artık liste sayfalarının adını da buraya yazıyor (tur:"sayfa");
     ayrım yapılmasaydı her listede geri oku belirirdi. */
  const kayitBasligi = Boolean(baslik?.ad) && baslik?.tur !== "sayfa";
  const detayda = dar && (kayitBasligi || detayRotasiMi(pathname));
  // Sayfa adı: PageHeader'ın bildirdiği başlık. Yoksa sistem adı.
  const sayfaAdi = baslik?.tur === "sayfa" ? baslik.ad : null;



  /* FAZ-91 — orta yuva seçiciye döner (Talepler taraf seçimi). */
  const seciciVar =
    dar && Array.isArray(baslik?.secenekler) && baslik.secenekler.length > 1;
  const [secMenuAcik, setSecMenuAcik] = useState(false);
  // Rota değişince menü kapanır — açık menüyle sayfa değişmesi kafa karıştırır.
  useEffect(() => {
    setSecMenuAcik(false);
  }, [pathname]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const profileUser = useSelector((state) => state.profile.user);
  const profilePhotoUrl = profileUser?.photo || user?.photo || null;

  // Active effective role: store’da yoksa localStorage fallback
  const activeEffectiveRoleFromStore = useSelector(
    (state) => state?.belediyeAuth?.activeEffectiveRole
  );
  const activeEffectiveRole = activeEffectiveRoleFromStore || safeGetActiveEffectiveRole();

  const sessionRole = useMemo(
    () => getSessionRole({ user, activeEffectiveRole }),
    [user, activeEffectiveRole]
  );

  //   NEW: Müdür ise birim adının yanına etiket ekle (mevcut yapıyı bozmadan)
  const displayUnitName = useMemo(() => {
    const base = String(unitName || "").trim();
    if (isSupervisor(sessionRole)) return base ? `${base} (Müdür)` : "(Müdür)";
    return base;
  }, [unitName, sessionRole]);

  // Navbar alt-başlık kaynağı:
  //  • all_units aktör (başkan / genel_admin / sistem_yoneticisi) → birim/workspace
  //    adı DEĞİL, ROL adı gösterilir ("Başkan" vb.) — TÜM modüllerde sabit. Böylece
  //    başkan Personel/Vatandaşlar/Sistem'e girince "Sistem Yönetimi" yazmaz.
  //    Kaynak: /me.global_role.name (yeni role_ref + legacy rolleri kapsar).
  //  • birim-kapsamlı kullanıcı → mevcut davranış (birim adı + "(Müdür)").
  const { uiOn: authzUiOn, scope: authzScope, globalRole } = usePermissions();
  const isAllUnitsActor = authzUiOn
    ? authzScope?.mode === "all_units"
    : isPresident(user);
  const scopeSubtitle = useMemo(() => {
    if (isAllUnitsActor) {
      const roleName = String(globalRole?.name || "").trim();
      if (roleName) return roleName; // "Başkan", "Genel Yönetici", "Sistem Yöneticisi"…
      if (isPresident(user)) return "Başkan"; // legacy fallback
      return "Tüm Birimler";
    }
    return displayUnitName;
  }, [isAllUnitsActor, globalRole, user, displayUnitName]);

  /* FAZ-97/4 — BİRİM/ROL ALT SATIRI YALNIZ SİSTEM ADININ ALTINDA.

     Kullanıcı (2026-08-01): "navbardaki birim adları veya başkan / başkan
     yardımcısı falan yazıyor ya; bunlar sadece yukarıda SİSTEMİN ADI
     yazılıyken olsun. Gidip de talep detay sayfasında altta 'Fen' falan
     yazmasın. Navbar navbar olarak kullanıldığında, yani 'Kırıkhan İletişim
     Merkezi' yazdığında orada o olsun; başka değil."

     Yani alt satır orta yuvanın NE gösterdiğine bağlı:
       • sistem adı  → birim/rol (kimlik bilgisi; "hangi şapkayla bakıyorum")
       • sayfa adı   → hiçbir şey (sayfa zaten kendini söylüyor)
       • kayıt adı   → kaydın numarası (o kaydı tanımlayan şey)

     Boşsa <span> HİÇ ÇİZİLMEZ: boş bir satır orta yuvayı iki satırlık
     yükseklikte tutar ve başlık dikeyde ortalanmamış görünürdü. */
  const ortaAlt = detayda
    ? baslik?.no || ""
    : sayfaAdi
    ? ""
    : scopeSubtitle;

  const hasMultipleUnits = useMemo(() => {
    const units = Array.isArray(user?.units) ? user.units : [];
    return units.length >= 2;
  }, [user]);

  const canSwitchUnit = useMemo(() => {
    if (!user) return false;
    if (isPresident(user)) return false;
    return hasMultipleUnits;
  }, [user, hasMultipleUnits]);

  const handleProfileClick = useCallback(() => {
    if (!profileUser) dispatch(fetchMyProfile());
    setMobileMenuOpen(false);

    // FAZ-22 4b: profil canonical (birimSİZ). Kabuk-özel olanlar (sistem/halk-masasi/
    // saha) kendi canonical kabuğunda; genel + başkan tek /yonetim/profil'e katlanır.
    const target = user?.is_superuser
      ? "/yonetim/sistem/profil"
      : isFieldStaff(sessionRole)
      ? "/yonetim/saha/profil"
      : (isDeskStaff(sessionRole) || user?.role === "personel_2")
      ? "/yonetim/halk-masasi/profil"
      : "/yonetim/profil";

    navigate(target, { replace: false });
  }, [dispatch, profileUser, navigate, unitSlug, user, sessionRole]);

  // ───────────────────────────────────────────────────────────────────────────
  // ÇIKIŞ — Tek kaynak: performLogout
  // ───────────────────────────────────────────────────────────────────────────
  // Akış:
  //   1. Önce Navbar'a özgü "active unit" alanlarını temizle
  //      (token/user vs. performLogout içinde temizlenir)
  //   2. performLogout çağır: backend bildir + localStorage temizle + /giris'e git
  // ───────────────────────────────────────────────────────────────────────────
  const handleLogout = useCallback(async () => {
    // Mobil menü açıksa kapat (UX için)
    setMobileMenuOpen(false);

    // Active unit alanları (Navbar'a özgü, performLogout bunları bilmiyor)
    try {
      clearActiveUnit();
    } catch {
      // ignore
    }

    // performLogout: backend bilgilendirme + localStorage + redirect
    await performLogout();
  }, []);

  const handleSwitchUnit = useCallback(() => {
    clearActiveUnit();
    setMobileMenuOpen(false);
    navigate("/yonetim/birim-sec", { replace: false });
  }, [navigate]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  //   Bildirim sayfası route’u (genel sistem)
  const handleGoNotificationsPage = useCallback(() => {
    setMobileMenuOpen(false);

    // Superuser başkanlık rotasına düşmez (başkan≠superuser) → sistem yönetimi.
    if (user?.is_superuser) {
      navigate("/yonetim/sistem/kullanicilar", { replace: false });
      return;
    }
    if (isPresident(user)) {
      navigate("/yonetim/bildirimler", { replace: false }); // FAZ-22 4b: canonical
      return;
    }

    // Halk masası için: şimdilik aynı sayfaya yönlendirme yoksa bile sorun çıkarmasın
    if (isDeskStaff(sessionRole) || user?.role === "personel_2") {
      // Eğer halk masası için ayrı bildirim sayfası açarsan burayı güncelleriz.
      // Şimdilik güvenli fallback: halk masası anasayfa.
      navigate("/yonetim/halk-masasi", { replace: false });
      return;
    }

    // unitSlug yoksa birim seç’e dön
    if (!unitSlug) {
      navigate("/yonetim/birim-sec", { replace: false });
      return;
    }

    navigate("/yonetim/bildirimler", { replace: false }); // FAZ-22 4b: canonical
  }, [navigate, unitSlug, user, sessionRole]);

  // ───────────────────────────────────────────────────────────────────────────
  // FAZ-88 — GERİ
  // Doğrudan URL ile gelen kullanıcıda geçmiş boş olabilir; o zaman
  // navigate(-1) hiçbir yere gitmez ve kullanıcı sıkışır. Böyle bir durumda
  // rotanın bir üst seviyesine (liste sayfasına) çıkılır.
  // ───────────────────────────────────────────────────────────────────────────
  const geriGit = useCallback(() => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    const ust = String(pathname).replace(/\/[^/]+\/?$/, "") || "/yonetim";
    navigate(ust, { replace: true });
  }, [navigate, pathname]);

  // ───────────────────────────────────────────────────────────────────────────
  // FAZ-88 — DAR KATMAN NAVBARI (telefon + tablet): ÜÇ YUVA
  //
  //   ☰ / ←        sistem adı + rol  ·  detayda kaydın adı        🔔
  //
  // Neden bu düzen:
  //  • Logo dar ekranda bilgi taşımıyordu ama başlığın kesilmesine sebep
  //    oluyordu (navbar-title-main ellipsis) → çizilmiyor.
  //  • Zil eskiden .desktop-actions içindeydi ve 768px'te display:none
  //    oluyordu: telefondan giren personel okunmamış sayacı HİÇ görmüyordu.
  //  • Profil dar katmanda navbardan çıkar; alt barın son yuvasıdır (K-3:
  //    "yukarıda tekrardan profil olmasın, aşağıda da bildirimler olmasın").
  //  • Kebap menü kaldırıldı: içindeki her madde artık ya çekmecede ya
  //    alt barda.
  // ───────────────────────────────────────────────────────────────────────────
  if (dar) {
    const saha = isFieldStaff(sessionRole);
    return (
      <nav className="navbar navbar--dar">
        <div className="navbar-sol">
          {detayda ? (
            <button
              type="button"
              className="navbar-yuva-btn"
              onClick={geriGit}
              aria-label="Geri"
              title="Geri"
            >
              <ChevronLeftIcon className="navbar-yuva-ikon" />
            </button>
          ) : sidebarEnabled && onSidebarToggle ? (
            <button
              type="button"
              className="navbar-yuva-btn"
              onClick={onSidebarToggle}
              aria-label="Menü"
              title="Menü"
              aria-expanded={sidebarExpanded ? "true" : "false"}
              aria-controls="belediye-sidebar"
            >
              <Bars3Icon className="navbar-yuva-ikon" />
            </button>
          ) : (
            /* Saha kabuğunda sidebar yok → hamburger de yok. Yuva boş kalır
               ki orta başlık gerçekten ortalansın. */
            <span className="navbar-yuva-bos" aria-hidden="true" />
          )}
        </div>

        {/* FAZ-91 — orta yuva SEÇİCİ olabilir.
            Taraf (Verdiklerim / Bana Gelenler) sayfanın KİMLİĞİdir, listenin
            süzgeci değil: başlıkta durunca "buradasın" der, çipte durunca
            "bunu süzüyorsun" der. Birincisi doğru. Ayrıca çip şeridi zaten
            5-6 çip taşıyor; taraf oraya girseydi 8 olurdu. */}
        {seciciVar ? (
          <button
            type="button"
            className="navbar-orta navbar-orta--secilir"
            onClick={() => setSecMenuAcik((a) => !a)}
            aria-haspopup="menu"
            aria-expanded={secMenuAcik ? "true" : "false"}
          >
            <span className="navbar-orta-bas">
              {baslik.ad}
              <ChevronDownIcon className="navbar-orta-ok" />
            </span>
            <span className="navbar-orta-alt">{baslik.no}</span>
          </button>
        ) : (
          <div className="navbar-orta">
            <span className="navbar-orta-bas">
              {detayda
                ? baslik?.ad || "Detay"
                : sayfaAdi || "Arsuz Çözüm Merkezi"}
            </span>
            {ortaAlt ? (
              <span className="navbar-orta-alt">{ortaAlt}</span>
            ) : null}
          </div>
        )}

        {seciciVar && secMenuAcik && (
          <>
            <div
              className="navbar-secperde"
              onClick={() => setSecMenuAcik(false)}
              aria-hidden="true"
            />
            <div className="navbar-secmenu pop-ok" data-ok="ust" role="menu">
              {baslik.secenekler.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={s.key === baslik.aktif}
                  className={s.key === baslik.aktif ? "etkin" : ""}
                  onClick={() => {
                    baslik.onSec?.(s.key);
                    setSecMenuAcik(false);
                  }}
                >
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="navbar-sag">
          {saha ? (
            /* Saha kullanıcısında bildirim zili bir açılır panel değil,
               sayfaya giden düğmedir (mevcut davranış korunuyor). */
            <button
              type="button"
              className="navbar-yuva-btn"
              onClick={handleGoNotificationsPage}
              aria-label="Bildirimler"
              title="Bildirimler"
            >
              <BellIcon className="navbar-yuva-ikon" />
            </button>
          ) : (
            <NotificationsBell />
          )}
        </div>
      </nav>
    );
  }

  // -------------------------------------------------------------
  // SAHA PERSONELİ NAVBAR
  // -------------------------------------------------------------
  if (isFieldStaff(sessionRole)) {
    return (
      <nav className="navbar">
        <div className="navbar-left">
          <img src={kimLogo} alt="Arsuz Belediyesi" className="navbar-logo" />
        </div>

        <div className="navbar-center mobile-title">
          <div className="navbar-title-main">Arsuz Çözüm Merkezi</div>
          <div className="navbar-title-sub">{scopeSubtitle}</div>
        </div>

        {/* Masaüstü aksiyonları — mobilde gizlenir, kebap menüye taşınır
            (yönetici navbar ile AYNI desen; saha'da ikonlar sağa yığılıp
            ortadaki başlıkla çakışıyordu). */}
        <div className="navbar-right desktop-actions">
          <NavLink
            to={`/yonetim/saha/gorevlerim`}
            className="navbar-icon-btn"
            title="Gelen Talepler"
            aria-label="Gelen Talepler"
            end
          >
            <HomeIcon />
          </NavLink>

          {/* FAZ-65: mesajlaşma ikonu navbar'dan KALDIRILDI (kullanıcı isteği).
              Sohbetin tek girişi sağ-alttaki başlatıcı balon — aynı işi yapan
              iki düğme, üstelik biri sayaçsız, gereksiz gürültüydü. */}

          {canSwitchUnit && (
            <button
              type="button"
              className="navbar-icon-btn"
              onClick={handleSwitchUnit}
              title="Birim Değiştir"
              aria-label="Birim Değiştir"
            >
              <BuildingOffice2Icon />
            </button>
          )}

          <button
            type="button"
            className="navbar-icon-btn"
            onClick={handleGoNotificationsPage}
            title="Bildirimler"
            aria-label="Bildirimler"
          >
            <BellIcon />
          </button>

          <button
            className="navbar-profile-avatar"
            onClick={handleProfileClick}
            aria-label="Profil"
            title="Profil"
          >
            {profilePhotoUrl ? (
              <img src={profilePhotoUrl} alt="Profil" className="navbar-profile-img" />
            ) : (
              <UserCircleIcon className="navbar-profile-icon" />
            )}
          </button>

          {/* Çıkış butonu — handleLogout prop'u ile özelleştirilmiş davranış */}
          <LogoutButton onLogout={handleLogout} />
        </div>

        {/* Mobil kebap menü butonu (sağ kenar) */}
        <button className="navbar-mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Menü">
          <EllipsisVerticalIcon className="navbar-mobile-menu-icon" />
        </button>

        {mobileMenuOpen && (
          <div className="mobile-menu-card">
            <button
              type="button"
              className="mobile-menu-item"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate(`/yonetim/saha/gorevlerim`, { replace: false });
              }}
            >
              <HomeIcon />
              <span>Gelen Talepler</span>
            </button>

            <button type="button" className="mobile-menu-item" onClick={handleGoNotificationsPage}>
              <BellIcon />
              <span>Bildirimler</span>
            </button>

            {canSwitchUnit && (
              <button type="button" className="mobile-menu-item" onClick={handleSwitchUnit}>
                <BuildingOffice2Icon />
                <span>Birim Değiştir</span>
              </button>
            )}

            <button
              type="button"
              className="mobile-menu-item"
              onClick={() => {
                setMobileMenuOpen(false);
                handleProfileClick();
              }}
            >
              <UserCircleIcon />
              <span>Profil</span>
            </button>

            {/* Mobil çıkış — desktop ile AYNI handleLogout */}
            <button type="button" className="mobile-menu-item" onClick={handleLogout}>
              <ArrowRightOnRectangleIcon />
              <span>Çıkış</span>
            </button>
          </div>
        )}
      </nav>
    );
  }

  // -------------------------------------------------------------
  // YÖNETİCİ / PERSONEL NAVBAR
  // -------------------------------------------------------------
  return (
    <nav className="navbar">
      <div className="navbar-left">
        <img src={kimLogo} alt="Arsuz Belediyesi" className="navbar-logo" />
      </div>

      <div className="navbar-center">
        <div className="navbar-title-main">Arsuz Çözüm Merkezi</div>
        <div className="navbar-title-sub">{scopeSubtitle}</div>
      </div>

      <div className="navbar-right desktop-actions">
        {/* Anasayfa artık sidebar'daki "Genel" bölümünde (navbar'dan kaldırıldı) */}

        {/* FAZ-51: görev göz-atma penceresi (NavbarTaskPeek) KALDIRILDI.
            Navbar'da üç ayrı sayaç vardı ve üçü üç farklı kaynaktan besleniyordu;
            görev sayacı zaten bir BİLDİRİM değil "üzerimdeki açık görev" sayısıydı
            (okunmuyor, azalmıyor, zille tutmuyor). Görev olayları artık yalnız
            zilde/bildirim sayfasında. */}

        {/* FAZ-65: mesajlaşma ikonu navbar'dan KALDIRILDI — bkz. yukarıdaki not. */}

        {/*   TEK SAYAÇ: bildirim zili (görev + mesaj + başvuru + duyuru) */}
        <NotificationsBell />

        {canSwitchUnit && (
          <button
            type="button"
            className="navbar-icon-btn"
            onClick={handleSwitchUnit}
            aria-label="Birim Değiştir"
            title="Birim Değiştir"
          >
            <BuildingOffice2Icon />
            <span>Birim Değiştir</span>
          </button>
        )}

        <button
          className="navbar-profile-avatar"
          onClick={handleProfileClick}
          aria-label="Profil"
          title="Profil"
        >
          {profilePhotoUrl ? (
            <img src={profilePhotoUrl} alt="Profil" className="navbar-profile-img" />
          ) : (
            <UserCircleIcon className="navbar-profile-icon" />
          )}
        </button>

        {/* Çıkış butonu — handleLogout prop'u ile özelleştirilmiş davranış */}
        <LogoutButton onLogout={handleLogout} />
      </div>

      {/* Mobil menü */}
      <button className="navbar-mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Menü">
        <EllipsisVerticalIcon className="navbar-mobile-menu-icon" />
      </button>

      {mobileMenuOpen && (
        <div className="mobile-menu-card">
          {/* Anasayfa artık sidebar'daki "Genel" bölümünde (navbar'dan kaldırıldı) */}

          {/*   Mobilde dropdown yerine sayfaya git (daha stabil) */}
          <button type="button" className="mobile-menu-item" onClick={handleGoNotificationsPage}>
            <BellIcon />
            <span>Bildirimler</span>
          </button>

          {canSwitchUnit && (
            <button type="button" className="mobile-menu-item" onClick={handleSwitchUnit}>
              <BuildingOffice2Icon />
              <span>Birim Değiştir</span>
            </button>
          )}

          <button
            type="button"
            className="mobile-menu-item"
            onClick={() => {
              setMobileMenuOpen(false);
              handleProfileClick();
            }}
          >
            <UserCircleIcon />
            <span>Profil</span>
          </button>

          {/* Mobil çıkış — desktop ile AYNI handleLogout, AYNI performLogout */}
          <button type="button" className="mobile-menu-item" onClick={handleLogout}>
            <ArrowRightOnRectangleIcon />
            <span>Çıkış</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default BelediyeNavbar;